import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PhoneInput } from '../components/ui/phone-input';
import { motion } from 'framer-motion';
import {
  Loader2,
  ClipboardList,
  Clock,
  Newspaper,
  TrendingUp,
  ArrowRight,
  Phone,
  Lock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.15 + i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }),
};

const LoginPage = () => {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detectedCountry, setDetectedCountry] = useState('TZ');

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) })
      .then(r => r.json())
      .then(d => { if (d.country_code) setDetectedCountry(d.country_code); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!phone || !phone.trim()) { setError('Please enter your phone number'); setLoading(false); return; }
    if (!pin || !pin.trim()) { setError('Please enter your PIN'); setLoading(false); return; }
    if (pin.length < 4) { setError('PIN must be at least 4 digits'); setLoading(false); return; }

    try {
      const cleanPhone = phone.replace(/\s/g, '');
      const result = await login(cleanPhone, pin);

      if (result.success) {
        navigate('/overview');
      } else {
        const errorMsg = result.error || '';
        if (errorMsg.toLowerCase().includes('locked') || errorMsg.toLowerCase().includes('try again in')) {
          setError(errorMsg);
        } else if (errorMsg.includes('Invalid phone number or PIN')) {
          setError('Incorrect phone number or PIN. Please check your credentials and try again.');
        } else if (errorMsg.includes('Network') || errorMsg.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection.');
        } else if (errorMsg.includes('not found')) {
          setError('Account not found. Please verify your phone number.');
        } else if (errorMsg.includes('deactivated')) {
          setError('Account is deactivated. Contact your administrator.');
        } else {
          setError(errorMsg || 'Login failed. Please try again.');
        }
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    }
    setLoading(false);
  };

  const features = [
    { icon: ClipboardList, title: 'Tasks & Follow-ups' },
    { icon: Clock, title: 'Attendance & Timesheets' },
    { icon: Newspaper, title: 'Newsroom Updates' },
    { icon: TrendingUp, title: 'Performance Dashboard' },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 relative overflow-hidden" style={{ background: '#0779BC', fontFamily: "'Work Sans', sans-serif" }} data-testid="login-page">

      {/* Floating decorative bubbles */}
      <div className="absolute top-8 left-10 w-5 h-5 rounded-full bg-white/20" />
      <div className="absolute top-12 left-14 w-2 h-2 rounded-full bg-white/30" />
      <div className="absolute bottom-16 right-12 w-6 h-6 rounded-full bg-white/15" />
      <div className="absolute bottom-20 right-20 w-2.5 h-2.5 rounded-full bg-white/25" />
      <div className="absolute top-1/3 right-8 w-3 h-3 rounded-full bg-white/20" />
      <div className="absolute bottom-1/4 left-6 w-4 h-4 rounded-full bg-white/15" />

      {/* Main white card */}
      <motion.div
        className="relative w-full max-w-[1050px] bg-white rounded-3xl shadow-2xl shadow-black/20 overflow-hidden"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        data-testid="login-form-card"
      >
        {/* Thin inner border effect */}
        <div className="absolute inset-3 sm:inset-4 rounded-2xl border border-[#0779BC]/[0.08] pointer-events-none z-10" />

        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[580px]">

          {/* ═══ LEFT — Illustration + Features ═══ */}
          <div className="hidden lg:flex flex-col items-center justify-center p-10 xl:p-14 relative bg-gradient-to-b from-slate-50/80 to-white">
            {/* Logo */}
            <motion.div
              className="mb-6"
              initial="hidden" animate="visible" custom={0} variants={fadeUp}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_9304be5e-5dfb-4406-9a75-7c4b3ad72b13/artifacts/f33mtxoy_ECOWATER_WORDMARK_LOGO.png"
                alt="Ecowater"
                className="h-7 mx-auto"
              />
            </motion.div>

            {/* Illustration */}
            <motion.div
              className="mb-8"
              initial="hidden" animate="visible" custom={0} variants={fadeUp}
            >
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9304be5e-5dfb-4406-9a75-7c4b3ad72b13/images/1455cb20857708b25380980754daf48a53b121726ab3cae6ec8e617a30b7185f.png"
                alt="Workspace illustration"
                className="w-64 xl:w-72 h-auto mx-auto drop-shadow-sm"
              />
            </motion.div>

            {/* Feature pills */}
            <motion.div
              className="flex flex-wrap justify-center gap-2.5"
              initial="hidden" animate="visible" custom={2} variants={fadeUp}
            >
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0779BC]/[0.06] border border-[#0779BC]/[0.1] hover:bg-[#0779BC]/[0.12] transition-colors cursor-default"
                  initial="hidden" animate="visible" custom={3 + i} variants={fadeUp}
                >
                  <f.icon className="h-3.5 w-3.5 text-[#0779BC]" />
                  <span className="text-xs font-medium text-[#0779BC]/80">{f.title}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* ═══ RIGHT — Login Form ═══ */}
          <div className="flex flex-col justify-center px-8 sm:px-12 xl:px-16 py-12 lg:py-0">
            {/* Mobile-only brand */}
            <div className="text-center mb-6 lg:hidden">
              <h1 className="text-xl font-bold text-slate-900">
                Ecowater <span className="text-[#0779BC]">Workspace</span>
              </h1>
            </div>

            <motion.div initial="hidden" animate="visible" custom={1} variants={fadeUp}>
              <h2 className="text-3xl sm:text-[2rem] font-bold text-slate-900 leading-tight" style={{ letterSpacing: '-0.02em' }}>
                <span className="text-[#0779BC]">Ecowater Workspace</span>
              </h2>
              <p className="text-sm text-slate-400 mt-2.5 leading-relaxed max-w-xs">
                Your daily hub for staying aligned, productive, and connected.
              </p>
            </motion.div>

            <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="login-form">
              {error && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                  <Alert variant="destructive" data-testid="login-error">
                    <AlertDescription className="text-sm">{error}</AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {/* Phone field */}
              <motion.div className="space-y-1" initial="hidden" animate="visible" custom={3} variants={fadeUp}>
                <div className="flex items-center gap-2.5 text-sm text-slate-400 mb-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  <span className="font-medium">Phone Number</span>
                </div>
                <PhoneInput
                  id="phone"
                  value={phone}
                  onChange={setPhone}
                  placeholder="712345678"
                  disabled={loading}
                  defaultCountry={detectedCountry}
                  data-testid="phone-input"
                />
              </motion.div>

              {/* PIN field */}
              <motion.div className="space-y-1" initial="hidden" animate="visible" custom={4} variants={fadeUp}>
                <div className="flex items-center gap-2.5 text-sm text-slate-400 mb-1.5">
                  <Lock className="h-3.5 w-3.5" />
                  <span className="font-medium">PIN</span>
                </div>
                <Input
                  id="pin"
                  type="password"
                  placeholder="Enter your PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  required
                  disabled={loading}
                  maxLength={4}
                  className="h-12 bg-transparent border-0 border-b-2 border-slate-200 rounded-none px-0 text-slate-900 text-base focus-visible:ring-0 focus-visible:border-[#0779BC] transition-colors placeholder:text-slate-300"
                  style={{ letterSpacing: '0.2em' }}
                  data-testid="pin-input"
                />
              </motion.div>

              {/* Submit */}
              <motion.div initial="hidden" animate="visible" custom={5} variants={fadeUp}>
                <Button
                  type="submit"
                  className="h-12 px-10 bg-[#0779BC] hover:bg-[#0667A1] text-white text-base font-medium rounded-full shadow-lg shadow-[#0779BC]/25 hover:shadow-xl hover:shadow-[#0779BC]/30 transition-all duration-200 active:scale-[0.97]"
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </motion.div>
            </form>

            {/* Footer */}
            <motion.p
              className="mt-8 text-sm text-slate-400"
              initial="hidden" animate="visible" custom={6} variants={fadeUp}
            >
              Need help? <span className="text-[#0779BC] font-medium cursor-pointer hover:underline">Contact your administrator</span>
            </motion.p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
